import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Star, Award } from "lucide-react";
import { Link } from "react-router-dom";
import { TID } from "@/constants/testIds";
import LogoMR from "@/components/landing/LogoMR";

const FRONT_COVER =
  "https://customer-assets.emergentagent.com/job_mare-method-shop-1/artifacts/xu3iw6tw_Immagine%202026-06-01%20154539.png";
const BACK_COVER =
  "https://customer-assets.emergentagent.com/job_mare-method-shop-1/artifacts/a0s1zr8n_Immagine%202026-06-01%20154633.png";

const SLIDES = [
  { src: FRONT_COVER, alt: "Metodo di Mare — copertina anteriore", label: "Copertina" },
  { src: BACK_COVER, alt: "Metodo di Mare — copertina posteriore", label: "Retro" },
];

const AMAZON_URL = "https://amzn.eu/d/0iLs59IO";

export default function Hero() {
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setSlide((s) => (s + 1) % SLIDES.length);
    }, 4500);
    return () => clearInterval(id);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid={TID.heroSection}
      className="relative pt-36 md:pt-44 pb-24 md:pb-32 overflow-hidden"
    >
      <div aria-hidden className="absolute inset-0 paper-grain pointer-events-none" />
      {/* Sunset gradient orbs */}
      <div
        aria-hidden
        className="absolute -top-20 -right-20 w-[55%] h-[80%] opacity-60 blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, #FFB47A 0%, #FF8A65 30%, transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="absolute top-1/3 -left-32 w-[40%] h-[60%] opacity-50 blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, #6EC1D9 0%, #0E7C8F 35%, transparent 70%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-7"
          >
            <div data-testid="hero-brand-logo" className="inline-flex items-center gap-4 mb-8">
              <LogoMR size={72} className="flex-shrink-0" />
              <span className="font-serif-display text-2xl md:text-3xl tracking-tight leading-none text-[#0B192C]">
                Metodo<span className="text-[#E89B6C] mx-1">·</span>di<span className="text-[#E89B6C] mx-1">·</span>Mare
              </span>
            </div>

            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={14}
                    fill="#D4B895"
                    strokeWidth={0}
                    className="text-[#D4B895]"
                  />
                ))}
              </div>
              <span className="overline">4,9/5 · 33 recensioni Amazon</span>
            </div>

            <h1 className="font-serif-display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight">
              Metodo di Mare.
              <br />
              Da militare a
              <br />
              <span className="italic text-[#0E7C8F]">imprenditore digitale.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg uppercase tracking-[0.2em] font-light text-[#3A4F6B]">
              Il libro · di Michele Roccaro
            </p>
            <p className="mt-6 max-w-xl text-lg md:text-xl font-light leading-relaxed text-[#3A4F6B]">
              Il metodo forgiato sull'Amerigo Vespucci e testato sul campo
              dell'imprenditoria: <strong className="text-[#0B192C] font-normal">15 capitoli operativi</strong> su metodo, leadership,
              cybersicurezza e vendita per costruire una carriera digitale
              solida.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-wrap">
              <a
                href={AMAZON_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={TID.heroCta}
                data-track="hero-cta-amazon"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-[#0B192C] text-[#F9F8F6] text-sm uppercase tracking-[0.2em] hover:bg-[#1E3557] transition-all"
              >
                Acquista su Amazon
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>
              <Link
                to="/certificazione"
                data-testid="hero-cta-certificazione"
                data-track="hero-cta-certificazione"
                className="group inline-flex items-center gap-3 px-7 py-4 border border-[#E89B6C] bg-[#E89B6C]/10 text-[#0B192C] text-sm uppercase tracking-[0.2em] hover:bg-[#E89B6C] hover:text-[#0B192C] transition-all"
              >
                <Award size={16} className="text-[#E89B6C] group-hover:text-[#0B192C]" />
                Diventa Professionista Digitale Certificato
              </Link>
              <Link
                to="/consulenza"
                data-testid="hero-cta-consulenza"
                data-track="hero-cta-consulenza"
                className="group inline-flex items-center gap-3 px-7 py-4 border border-[#0B192C] text-[#0B192C] text-sm uppercase tracking-[0.2em] hover:bg-[#0B192C] hover:text-[#F9F8F6] transition-all"
              >
                Consulenza Aziendale e Professionisti
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </Link>
            </div>

            <button
              data-testid={TID.heroSecondary}
              onClick={() => scrollTo("method")}
              className="mt-4 text-sm uppercase tracking-[0.2em] link-underline text-[#0B192C]"
            >
              Scopri il metodo
            </button>

            <div className="mt-14 grid grid-cols-3 gap-8 max-w-md">
              <Stat number="188" label="Pagine" />
              <Stat number="09" label="Anni Vespucci" />
              <Stat number="4,9" label="Su Amazon" />
            </div>
          </motion.div>

          {/* Right - book cover slideshow */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-5 relative"
          >
            <div className="relative mx-auto max-w-sm">
              <div
                aria-hidden
                className="absolute -top-10 -left-6 font-serif-display text-[140px] leading-none text-[#D4B895]/30 select-none"
              >
                01
              </div>

              <div
                className="relative aspect-[3/4] shadow-[0_30px_80px_-30px_rgba(11,25,44,0.55)] border border-[#0B192C]/10 overflow-hidden bg-[#0B192C]"
                data-testid="hero-book-slideshow"
              >
                <AnimatePresence mode="sync">
                  <motion.img
                    key={slide}
                    src={SLIDES[slide].src}
                    alt={SLIDES[slide].alt}
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </AnimatePresence>

                {/* Slide indicator */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                  {SLIDES.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => setSlide(i)}
                      data-testid={`hero-slide-dot-${i}`}
                      aria-label={s.label}
                      className={`h-1 transition-all ${
                        i === slide
                          ? "w-8 bg-[#D4B895]"
                          : "w-4 bg-[#F9F8F6]/40 hover:bg-[#F9F8F6]/70"
                      }`}
                    />
                  ))}
                </div>
              </div>

              <div className="absolute -bottom-6 -right-4 bg-[#F9F8F6] border border-[#0B192C] px-5 py-3">
                <p className="overline text-[#0B192C]">Disponibile ora</p>
                <p className="font-serif-display text-2xl mt-1">€19,90</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Stat({ number, label }) {
  return (
    <div className="border-t border-[#E5E1DA] pt-4">
      <p className="font-serif-display text-3xl text-[#0B192C]">{number}</p>
      <p className="overline mt-1 text-[#3A4F6B]">{label}</p>
    </div>
  );
}
